import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Inject,
  forwardRef
} from '@angular/core';
import { ActivityView } from '../../../../../views/master-views/app.view/app.view.component';
import { SearchParcelDataActivity } from '../../../../../models/activities';
import { DataService, WorkflowContextService } from '../../../../../services';
import { EsriMapComponent } from 'src/app/components/arcgis/esri-map/esri-map.component';
import { MapService } from 'src/app/models';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
    selector: 'wm-search-parcel-data-activity-view',
    templateUrl: './search-parcel-data-activity-view.component.html',
    styleUrls: ['./search-parcel-data-activity-view.component.css'],
    standalone: false
})
export class SearchParcelDataActivityViewComponent extends ActivityView
  implements OnInit {
  @ViewChild(EsriMapComponent, { static: false })
  private esriMap: EsriMapComponent;

  @Input()
  public activity: SearchParcelDataActivity;
  templateForPermitForm: boolean = false
  public results: { [key: string]: string }[];
  // Added by amisha on 26-05-2026 to store a printable parcel map image
  // because the live ArcGIS map does not render reliably in browser print.
  public mapPrintImageUrl: string = null;
  private _cachedMapService: MapService;

  constructor(
    public layoutService: LayoutService,
    @Inject(forwardRef(() => DataService)) private _dataSvc: DataService,
    context : WorkflowContextService
  ) {
    super();

       layoutService.useNewLayout$.subscribe((useNewLayout) => { 
      this.templateForPermitForm = useNewLayout;
    });
  }

  ngOnInit() {
    if (this.activity.model.selectedParcel) {
      this.results = [
        {
          parcelID: 'ParcelID',
          address: 'Address',
          city: 'City',
          ownerName: 'OwnerName',
          acres: 'Acres'
        },
        <any>this.activity.model.selectedParcel
      ];
    }
  }

  async getParcelLayerInfo(): Promise<MapService> {
    if (!this._cachedMapService) {
      this._cachedMapService = await this._dataSvc
        .getMapService(this.activity.model.mapConfig.mapServiceId)
        .toPromise();
    }
    return this._cachedMapService;
  }

  async onMapLoaded() {
    const ms = await this.getParcelLayerInfo();
    if (this.esriMap) {
      await this.esriMap.enableFeatureSelection(
        ms.parcelLayer,
        ms.parcelLayerKeyField,
        true
      );

      const parcelIds = this.results
        .filter((parcel, i) => i > 0) // skip the header row
        .map(parcel => parcel.parcelID);

      await this.esriMap.selectFeaturesOnMap(parcelIds, 'redlayer');


      if (this.activity.featureGeometryWKT) {
        const keys = Object.keys(
          this.activity.featureGeometryWKT
        ).filter(f => f != '$id');
        const featureWkt: string[] = [];
        keys.forEach(k => {
          if (this.activity.featureGeometryWKT[k]) {
            this.activity.featureGeometryWKT[k].forEach(f => {
              if (f.item2) {
                featureWkt.push(f.item2);
              }
            });
          }
        });

        await this.esriMap.setReferenceData(featureWkt);
        await this.esriMap.zoomToInitialExtentFull();
      }
    }
  }

  // Added by amisha on 26-05-2026 to capture the parcel map as an image
  // only for print so the summary can show the map even when canvas/WebGL does not print.
  private async capturePrintMapImage(retryCount = 0) {
    if (!this.esriMap) {
      return;
    }

    await this.esriMap.waitForRenderComplete();

    try {
      const imageUrl = await this.esriMap.getScreenshotURL();

      if (imageUrl) {
        this.mapPrintImageUrl = imageUrl;
        return;
      }
    } catch (error) {
    }

    if (retryCount < 2) {
      await this.capturePrintMapImage(retryCount + 1);
    }
  }

    parcelTableColumns = [

    {
      key: 'parcelID',
      label: 'Parcel ID',
      type: 'text'
    },
    {
      key: 'address',
      label: 'Address',
      type: 'text'
    },
    {
      key: 'city',
      label: 'City',
      type: 'text'
    },
    {
      key: 'ownerName',
      label: 'Owner Name',
      type: 'text'
    },
    {
      key: 'acres',
      label: 'Acres',
      type: 'text'
    }
  
  ];
  
  
  
  
  trimResultsByColumns(data: any[], columns: any[]) {
    return data.map((item, index) => {
      return columns.reduce((acc: any, col: any) => {
        const key = col.key;
  
        if (item.hasOwnProperty(key)) {
          if(key === 'acres'){
  
            acc[key] = parseFloat(item[key]).toFixed(3);
          }
          else  
          {
            acc[key] = item[key];
          }
        }
  
        return acc;
      }, {});
    });
  }


  async persistChildComponent() {
    this.activity.featureGeometryWKT = null;
    return this.esriMap.persistChildComponent();
  }

  // Added by amisha on 26-05-2026 to prepare the printable parcel map image
  // only during print preparation so we do not run screenshot work on every persist.
  async prepareForPrint() {
    // Added by amisha on 02-06-2026 to keep heavy screenshot generation
    // in a print-only hook instead of persistChildComponent().
    await this.capturePrintMapImage();
    return null;
  }

}
